import { sniffImage, type SniffedType } from "./imageSniff";
import { checkMediaUrl } from "./mediaUrl";

/**
 * Loads ONE approved media derivative for a sign or billboard (M5, ADR-0009). Every step refuses
 * rather than guesses:
 *  1. the URL must pass the same-origin `/api/v1/media/` allow-list (mediaUrl.ts);
 *  2. fetch with mode "same-origin" and redirect "error" (a redirect elsewhere fails);
 *  3. HTTP 2xx and a `Content-Type` of image/png or image/webp;
 *  4. at most `maxBytes`, enforced while streaming the body;
 *  5. the magic bytes must match the declared type, and the header's pixel size must be within
 *     `maxPixels` per side BEFORE decoding;
 *  6. decode once, downscaled to fit `fit` (never upscaled), first frame only.
 * The result is a bitmap the painters draw onto a canvas texture (contain-fit, never distorted,
 * never recoloured). Errors carry a short code for diagnostics; callers fall back to the generated
 * sign (monogram / title) and never surface them as scene errors.
 */
export interface DecodedImage {
  readonly source: CanvasImageSource;
  readonly width: number;
  readonly height: number;
  /** The image's corner colour when all four corners are the same opaque colour (its padding), else null. */
  readonly edgeColor: string | null;
  close(): void;
}

export type MediaLoadErrorCode = "rejected_url" | "network" | "http" | "redirected" | "bad_type" | "too_large" | "bad_image" | "too_many_pixels" | "decode_failed" | "aborted";

export class MediaLoadError extends Error {
  constructor(
    readonly code: MediaLoadErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "MediaLoadError";
  }
}

export interface MediaLoadOptions {
  maxBytes: number;
  /** Largest accepted width or height in the file header (ADR-0009: 2048 logos, 4096 billboards). */
  maxPixels: number;
  /** Decode size budget: the bitmap is downscaled to fit inside this box. */
  fit: { width: number; height: number };
  signal?: AbortSignal;
}

export interface MediaLoaderDeps {
  origin: string;
  fetch(url: string, init: RequestInit): Promise<Response>;
  decode(bytes: Uint8Array, type: SniffedType, width: number, height: number): Promise<DecodedImage>;
}

const ACCEPTED_TYPES: readonly SniffedType[] = ["image/png", "image/webp"];

/** Contain-fit (w, h) inside (maxW, maxH) without upscaling; at least 1 x 1. */
export function fitWithin(width: number, height: number, maxW: number, maxH: number): { width: number; height: number } {
  const s = Math.min(1, maxW / width, maxH / height);
  return { width: Math.max(1, Math.round(width * s)), height: Math.max(1, Math.round(height * s)) };
}

/** "#RRGGBB" when the four corner pixels (RGBA, 16 values) are one opaque colour, else null. */
export function uniformEdgeColor(rgba: ArrayLike<number>): string | null {
  if (rgba.length < 16) return null;
  const px = [0, 4, 8, 12].map((o) => [rgba[o]!, rgba[o + 1]!, rgba[o + 2]!, rgba[o + 3]!] as const);
  if (px.some((p) => p[3] < 250)) return null;
  for (let c = 0; c < 3; c++) {
    const vals = px.map((p) => p[c]!);
    if (Math.max(...vals) - Math.min(...vals) > 10) return null;
  }
  const avg = (c: number) => Math.round(px.reduce((a, p) => a + p[c]!, 0) / 4);
  return `#${[avg(0), avg(1), avg(2)].map((v) => v.toString(16).padStart(2, "0").toUpperCase()).join("")}`;
}

function contentType(res: Response): string {
  return (res.headers.get("content-type") ?? "").split(";")[0]!.trim().toLowerCase();
}

async function readLimited(res: Response, maxBytes: number): Promise<Uint8Array> {
  const declared = Number(res.headers.get("content-length") ?? "NaN");
  if (Number.isFinite(declared) && declared > maxBytes) throw new MediaLoadError("too_large", `declared ${declared} bytes > ${maxBytes}`);
  const body = res.body;
  if (!body) {
    const buf = new Uint8Array(await res.arrayBuffer());
    if (buf.byteLength > maxBytes) throw new MediaLoadError("too_large", `${buf.byteLength} bytes > ${maxBytes}`);
    return buf;
  }
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > maxBytes) {
      await reader.cancel().catch(() => undefined);
      throw new MediaLoadError("too_large", `more than ${maxBytes} bytes`);
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.byteLength;
  }
  return out;
}

export async function loadMediaImage(rawUrl: string, opts: MediaLoadOptions, deps: MediaLoaderDeps): Promise<DecodedImage> {
  const checked = checkMediaUrl(rawUrl, deps.origin);
  if (!checked.ok) throw new MediaLoadError("rejected_url", checked.reason);
  const aborted = () => opts.signal?.aborted === true;
  let res: Response;
  try {
    res = await deps.fetch(checked.url, {
      method: "GET",
      mode: "same-origin",
      credentials: "same-origin",
      redirect: "error",
      referrerPolicy: "same-origin",
      headers: { Accept: "image/webp,image/png" },
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (err) {
    if (aborted()) throw new MediaLoadError("aborted", "aborted");
    throw new MediaLoadError("network", err instanceof Error ? err.message : String(err));
  }
  if (res.redirected) throw new MediaLoadError("redirected", "redirected response");
  if (!res.ok) throw new MediaLoadError("http", `HTTP ${res.status}`);
  const declaredType = contentType(res);
  if (!ACCEPTED_TYPES.includes(declaredType as SniffedType)) throw new MediaLoadError("bad_type", `content-type ${declaredType || "(none)"}`);
  let bytes: Uint8Array;
  try {
    bytes = await readLimited(res, opts.maxBytes);
  } catch (err) {
    if (err instanceof MediaLoadError) throw err;
    if (aborted()) throw new MediaLoadError("aborted", "aborted");
    throw new MediaLoadError("network", err instanceof Error ? err.message : String(err));
  }
  const sniffed = sniffImage(bytes);
  if (!sniffed) throw new MediaLoadError("bad_image", "not a PNG or WebP file");
  if (sniffed.type !== declaredType) throw new MediaLoadError("bad_type", `bytes are ${sniffed.type}, declared ${declaredType}`);
  if (sniffed.width > opts.maxPixels || sniffed.height > opts.maxPixels) throw new MediaLoadError("too_many_pixels", `${sniffed.width}x${sniffed.height} > ${opts.maxPixels}`);
  if (aborted()) throw new MediaLoadError("aborted", "aborted");
  const target = fitWithin(sniffed.width, sniffed.height, opts.fit.width, opts.fit.height);
  let image: DecodedImage;
  try {
    image = await deps.decode(bytes, sniffed.type, target.width, target.height);
  } catch (err) {
    throw new MediaLoadError("decode_failed", err instanceof Error ? err.message : String(err));
  }
  if (aborted()) {
    image.close();
    throw new MediaLoadError("aborted", "aborted");
  }
  return image;
}

/** Corner colour of a bitmap (drawn into a 2 x 2 scratch canvas), or null when unavailable. */
function sampleEdgeColor(bitmap: ImageBitmap): string | null {
  try {
    const w = bitmap.width;
    const h = bitmap.height;
    const canvas = typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(2, 2) : Object.assign(document.createElement("canvas"), { width: 2, height: 2 });
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, 2, 2);
    ctx.drawImage(bitmap, 0, 0, 1, 1, 0, 0, 1, 1);
    ctx.drawImage(bitmap, w - 1, 0, 1, 1, 1, 0, 1, 1);
    ctx.drawImage(bitmap, 0, h - 1, 1, 1, 0, 1, 1, 1);
    ctx.drawImage(bitmap, w - 1, h - 1, 1, 1, 1, 1, 1, 1);
    return uniformEdgeColor(ctx.getImageData(0, 0, 2, 2).data);
  } catch {
    return null;
  }
}

/** Browser dependencies: window.fetch, createImageBitmap (downscaled decode) and the page origin. */
export function browserMediaDeps(origin: string): MediaLoaderDeps {
  return {
    origin,
    fetch: (url, init) => fetch(url, init),
    decode: async (bytes, type, width, height) => {
      const blob = new Blob([bytes as BlobPart], { type });
      const bitmap = await createImageBitmap(blob, { resizeWidth: width, resizeHeight: height, resizeQuality: "high" });
      const edgeColor = sampleEdgeColor(bitmap);
      let closed = false;
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        edgeColor,
        close: () => {
          if (closed) return;
          closed = true;
          bitmap.close();
        },
      };
    },
  };
}
